import { Myna } from 'myna-parser';

export class LuceneGrammar {
    private Myna: any = Myna;

    public delayedQuery = this.Myna.delay(() => this.query);
    public whitespace = this.Myna.char(' \t\n\r\f').oneOrMore;
    public ws = this.Myna.char(' \t\n\r\f').zeroOrMore;
    public escapedChar = this.Myna.char('\\').advance;
    public float = this.Myna.digit.zeroOrMore.then(this.Myna.seq('.', this.Myna.digits).opt).ast;

    public modifier = this.Myna.char('+-').ast;

    public symbolicOperator = this.Myna.choice('||', '&&', '!');
    public operator = this.Myna.keywords('OR NOT', 'AND NOT', 'OR', 'AND', 'NOT').or(this.symbolicOperator).opt.ast;

    // Represents valid termchars
    // NOTE: according to the specification additional characters are not accepted: ':/&|' however, many of these
    // interfere with date parsing.
    public termChar = this.Myna.seq(this.symbolicOperator.not, this.Myna.notChar(' \t\r\n\f{}()"^~[]+-:\\')).or(
        this.escapedChar
    );

    public singleTerm = this.termChar.oneOrMore.ast;
    public fieldName = this.termChar.unless(this.Myna.char(':/')).oneOrMore.ast;
    public field = this.fieldName.then(':').then(this.ws);

    public phrase = this.Myna.doubleQuoted(this.Myna.notChar('"').zeroOrMore).ast;
    public regex = this.Myna.seq('/', this.Myna.notChar('/').zeroOrMore, '/').ast;

    public openParens = this.Myna.text('(').then(this.ws);
    public closeParens = this.ws.then(this.Myna.text(')'));

    public group = this.Myna.seq('(', this.delayedQuery, this.Myna.assert(')')).ast;

    public colon = this.Myna.choice(this.Myna.char(':'), this.Myna.text('\\:')).ast;
    public dash = this.Myna.choice(this.Myna.char('-'), this.Myna.text('\\-')).ast;

    public date = this.Myna.seq(
        this.Myna.digit.repeat(4),
        this.dash,
        this.Myna.digit.repeat(2),
        this.dash,
        this.Myna.digit.repeat(2)
    );

    public time = this.Myna.seq(
        this.Myna.digit.repeat(2),
        this.colon,
        this.Myna.digit.repeat(2),
        this.colon,
        this.Myna.digit.repeat(2),
        this.Myna.seq('.', this.Myna.digit.quantified(3, 6)).opt
    );

    public iso8601 = this.Myna.seq(this.date, 'T', this.time, 'Z').ast;
    public wildcard = this.Myna.char('*').ast;

    public endPoint = this.Myna.seq(this.ws, this.Myna.choice(this.iso8601, this.wildcard, this.singleTerm), this.ws);

    public inclusiveRange = this.Myna.seq('[', this.endPoint, this.Myna.keyword('TO'), this.endPoint, ']').ast;
    public exclusiveRange = this.Myna.seq('{', this.endPoint, this.Myna.keyword('TO'), this.endPoint, '}').ast;
    public exclusiveStartRange = this.Myna.seq('{', this.endPoint, this.Myna.keyword('TO'), this.endPoint, ']').ast;
    public exclusiveEndRange = this.Myna.seq('[', this.endPoint, this.Myna.keyword('TO'), this.endPoint, '}').ast;

    public range = this.Myna.choice(
        this.inclusiveRange,
        this.exclusiveRange,
        this.exclusiveStartRange,
        this.exclusiveEndRange
    ).ast;

    public termValue = this.Myna.choice(this.singleTerm, this.phrase, this.regex, this.range).ast;

    public valueModifier = this.modifier.ast;

    public leafTerm1 = this.Myna.seq(
        this.modifier,
        this.openParens,
        this.field.opt,
        this.valueModifier.opt,
        this.termValue,
        this.closeParens
    );

    public leafTerm2 = this.Myna.seq(this.modifier, this.field.opt, this.valueModifier.opt, this.termValue);

    public leafTerm3 = this.Myna.seq(
        this.modifier,
        this.field.opt,
        this.openParens,
        this.valueModifier.opt,
        this.termValue,
        this.closeParens
    );

    public leafTerm4 = this.Myna.seq(this.openParens.opt, this.termValue, this.closeParens.opt);

    public leafTerm = this.Myna.choice(this.leafTerm1, this.leafTerm2, this.leafTerm3, this.leafTerm4).ast;

    public matchAllNested = this.Myna.seq(
        '+',
        this.field,
        this.openParens,
        this.Myna.seq('+', this.termValue).delimited(this.whitespace),
        this.closeParens
    ).ast;

    public matchNoneNested = this.Myna.seq(
        '-',
        this.field,
        this.openParens,
        this.Myna.seq(this.termValue).delimited(this.whitespace),
        this.closeParens
    ).ast;

    public matchAtLeastOne = this.Myna.seq(
        '+',
        this.field,
        this.openParens,
        this.Myna.seq(this.termValue).delimited(this.whitespace),
        this.closeParens
    ).ast;

    public nestedTerm = this.Myna.seq(
        this.modifier.opt,
        this.openParens.opt,
        this.field.opt,
        this.modifier.opt,
        this.group,
        this.closeParens.opt
    ).ast;

    public term = this.Myna.choice(
        this.leafTerm,
        this.matchAllNested,
        this.matchNoneNested,
        this.matchAtLeastOne,
        this.nestedTerm
    );

    // ApiQuery
    public terms = this.Myna.delimited(this.term, this.ws).ast;
    public query = this.Myna.seq(this.ws, this.terms, this.ws).ast;
}
